import { toast } from "react-toastify";
import { warehouseApiClient } from "../../../services/warehouseClient";
import { throwError } from "../../../utils/errorHandling";
import { IResponse, IStandardResponse } from "../../orders/models/order";
import {
  IAccWarehouseInvoice,
  ICategoryAccessory,
} from "../models/accWarehouse";
import {
  IApprovedDn,
  IApprovedDnGrouped,
  IAuditLogDN,
  IInputStock,
  ILocation,
  IStockDataResponse,
  IStockDetails,
  IUserProgress,
  Tag,
} from "../models/stock";
import { IWarehouseInvoice, IYarn } from "../models/warehouse";

export enum EWarehouseEndPoint {
  ALL_YARN_INVOICE = "invoices/yarn/",
  YARN_INVOICE = "invoices/invoice/yarn/",
  ALL_YARN = "yarn/all_yarn/",
  ALL_ACCESSORY = "accessory",
  STOCK_HISTORY = "stock/history/",
  ALL_STOCK = "stock/",
  ALL_APPROVED = "delivery-note/approved/",
  ALL_LOCATION = "location/",
  ALL_STOCK_AVAILABLE = "stock/all_available/",
  ALL_STOCK_AVAILABLE_BY_IDS = "stock/all_available_by_ids/",
  KEY_APPROVED = "approvedDN/",
  ALL_ACC_INVOICE = "invoices/accessory/",
  ACC_INVOICE = "invoices/invoice/accessory/",
  APPROVED_DN_STOCK_ISSUE = "delivery-note/approved_grouped/",
  AUDIT_LOG = "auditlogs/get_history/",
  CATEGORY_PROVIDER = "data-provider/accessory/categories",
  TRACKING_LOG="auditlogs/log/",
  UPLOAD_STOCK = "uploadExcel/upload",
  STOCK_PROGRESS = "uploadExcel/progress",
  TAG_ALL = "stock/tag/",
  CREATE_TAG = "tag/create",
  UPDATE_TAG = "tag/update/",
  DELETE_TAG = "tag/delete/",
  REFERENCE = "accessory/references",
  GARMENT_CREATE = "garment",
  ADJUSTMENT = "stock-adjustment",
}

export const warehouseApi = {
  getAllYarnInvoice: async (): Promise<
    IStandardResponse<Array<IWarehouseInvoice>>
  > => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<Array<IWarehouseInvoice>>
      >(EWarehouseEndPoint.ALL_YARN_INVOICE);

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getAllAccInvoice: async (): Promise<
    IStandardResponse<Array<IWarehouseInvoice>>
  > => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<Array<IWarehouseInvoice>>
      >(EWarehouseEndPoint.ALL_ACC_INVOICE);

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getAllYarn: async (): Promise<IStandardResponse<Array<IYarn>>> => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<Array<IYarn>>
      >(EWarehouseEndPoint.ALL_YARN);

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getAllExistingYarnAccLycra: async (
    type: "yarn" | "accessory" | "lycra"
  ): Promise<IStandardResponse<Array<IStockDetails>>> => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<Array<IStockDetails>>
      >(
        type === "yarn"
          ? EWarehouseEndPoint.ALL_YARN
          : type === "accessory"
          ? EWarehouseEndPoint.ALL_ACCESSORY
          : ""
      );

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getPackingByYarnInvoice: async (
    num_invoice: string
  ): Promise<IStandardResponse<IWarehouseInvoice>> => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<IWarehouseInvoice>
      >(`${EWarehouseEndPoint.YARN_INVOICE}${num_invoice}`);

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getPackingByAccInvoice: async (
    num_invoice: string
  ): Promise<IStandardResponse<IAccWarehouseInvoice>> => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<IAccWarehouseInvoice>
      >(`${EWarehouseEndPoint.ACC_INVOICE}${num_invoice}`);

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getHistoryById: async <T>(id: string, type: string) => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<Array<T>>
      >(`${EWarehouseEndPoint.STOCK_HISTORY}${type}/${id}`);

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getAllStock: async (
    page: number = 1,
    limit: number = 10,
    search: Record<string, any>,
    sort: string,
    global_search?: string,
    category?: string
  ): Promise<IResponse<IStockDataResponse>> => {
    const categoryParam = category ? `${category}` : "";
    try {
      const { data } = await warehouseApiClient.get<
        IResponse<IStockDataResponse>
      >(`${EWarehouseEndPoint.ALL_STOCK}${categoryParam}/all`, {
        params: {
          page,
          limit,
          search,
          sort,
          global_search,
        },
      });
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getAllApprovedDN: async (
    page: number = 1,
    limit: number = 10,
    search: Record<string, any>,
    sort: string,
    global_search?: string,
    category?: string,
    fetchType?: string
  ): Promise<IResponse<Array<IApprovedDn>>> => {
    const categoryParam =
      category && fetchType ? `${category}/${fetchType}` : "";
    try {
      const { data } = await warehouseApiClient.get<
        IResponse<Array<IApprovedDn>>
      >(`${EWarehouseEndPoint.ALL_APPROVED}${categoryParam}`, {
        params: {
          page,
          limit,
          search,
          sort,
          global_search,
        },
      });

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getAllLocation: async () => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<Array<ILocation>>
      >(`${EWarehouseEndPoint.ALL_LOCATION}`);

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getAllStockAvailable: async (
    page: number = 1,
    limit: number = 10,
    search: Record<string, any>,
    sort: string,
    global_search?: string,
    category?: string
  ): Promise<IResponse<IStockDataResponse>> => {
    const categoryParam = category ? `${category}` : "";
    try {
      const { data } = await warehouseApiClient.get<
        IResponse<IStockDataResponse>
      >(`${EWarehouseEndPoint.ALL_STOCK_AVAILABLE}${categoryParam}`, {
        params: {
          page,
          limit,
          search,
          sort,
          global_search,
        },
      });
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  
  getAllStockAvailableByIds: async (
    page: number = 1,
    limit: number = 10,
    search: Record<string, any>,
    sort: string,
    global_search?: string,
    category?: string
  ): Promise<IResponse<IStockDataResponse>> => {
    const categoryParam = category ? `${category}` : "";
    try {
      const { data } = await warehouseApiClient.post<
        IResponse<IStockDataResponse>
      >(`${EWarehouseEndPoint.ALL_STOCK_AVAILABLE_BY_IDS}${categoryParam}`, {
          page,
          limit,
          search,
          sort,
          global_search,
      });
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },

  getYarnStockForSelection: async (
    search: Record<string, unknown>
  ): Promise<IResponse<IStockDataResponse>> => {
    try {
      const { data } = await warehouseApiClient.post<
        IResponse<IStockDataResponse>
      >(`${EWarehouseEndPoint.ALL_STOCK_AVAILABLE_BY_IDS}yarn`, {
        page: 1,
        limit: 50,
        search,
        sort: "id,desc",
        global_search: "",
      });

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },

  getAccessoryStockForSelection: async (
    search: Record<string, unknown>
  ): Promise<IResponse<IStockDataResponse>> => {
    try {
      const { data } = await warehouseApiClient.post<
        IResponse<IStockDataResponse>
      >(`${EWarehouseEndPoint.ALL_STOCK_AVAILABLE_BY_IDS}accessory`, {
        page: 1,
        limit: 50,
        search,
        sort: "id,desc",
        global_search: "",
      });

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },

  getApprovedDnGrouped: async (category: string, fetchType: string) => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<Array<IApprovedDnGrouped>>
      >(
        `${EWarehouseEndPoint.APPROVED_DN_STOCK_ISSUE}${category}/${
          fetchType || ""
        }`
      );

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  updateStockDetails: async <T>(
    id: number,
    type: string,
    input: Omit<IInputStock, "location" | "id">
  ): Promise<IStandardResponse<T>> => {
    try {
      const { data } = await warehouseApiClient.patch<IStandardResponse<T>>(
        `${type}/${id}`,
        { ...input }
      );

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  createManualRequest: async <T>(
    type: string,
    input: Omit<IInputStock, "id" | "stock_id">
  ): Promise<IStandardResponse<T>> => {
    try {
      const { data } = await warehouseApiClient.post<IStandardResponse<T>>(
        `${EWarehouseEndPoint.ALL_STOCK}${type}`,
        { ...input }
      );

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getAuditLogs: async (type: string, id: number) => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<Array<IAuditLogDN>>
      >(`${EWarehouseEndPoint.AUDIT_LOG}${type}/${id}`);

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },
  getCategoryProvider: async () => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<Array<ICategoryAccessory>>
      >(`${EWarehouseEndPoint.CATEGORY_PROVIDER}`);

      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },

  orderfetchLogFile:async(orderCode: string):Promise<any>=>{
    try{
      const response= await warehouseApiClient.get(
        `${EWarehouseEndPoint.TRACKING_LOG}${orderCode}`,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data
    }catch (error) {
      throw new Error(throwError(error));
    }
  },

  uploadStock: async (file: FormData): Promise<IStandardResponse<Omit<IInputStock, "id" | "stock_id">>> => {
    try {
      const { data } = await warehouseApiClient.post<IStandardResponse<Omit<IInputStock, "id" | "stock_id">>>(
        `${EWarehouseEndPoint.UPLOAD_STOCK}`,
        file,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (!data) {
        throw new Error("No data returned from the server");
      }
      toast.success("Stock successfully created.");
      return data;
      
    } catch (error: any) {
        const messages = Array.isArray(error?.error?.message)
          ? error.error.message
          : error?.error?.message || "Creation stock failed";
        toast.error(messages);
        throw new Error(error);
    }
   },

  getProgress: async (): Promise<IStandardResponse<IUserProgress>> => {
    try {
      const { data } = await warehouseApiClient.get<
        IStandardResponse<IUserProgress>
      >(EWarehouseEndPoint.STOCK_PROGRESS);
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },

  getAllTag: async (): Promise<IStandardResponse<Array<Tag>>> => {
    try {

      const { data } = await warehouseApiClient.get<
        IStandardResponse<Array<Tag>>
      >(`${EWarehouseEndPoint.TAG_ALL}`);
      return data;
    } catch (error: any) {
      console.warn(error);
      throw new Error(error);
    }
  },

  updateTag: async (
    id: number,
    input: Partial<Tag>
  ): Promise<IStandardResponse<Tag>> => {
    try {
      const { data } = await warehouseApiClient.patch<
        IStandardResponse<Tag>
      >(`${EWarehouseEndPoint.UPDATE_TAG}${id}`, input);
      if (!data) {
        throw new Error("No data returned from the server");
      }
      toast.success("Tag successfully updated.");
      return data;
    } catch (error: any) {
      toast.error(`${error.error.message}` || `${error.message}` ||  "Update tag failed");
      throw new Error(error);
    }
  },

  createTag: async (
   tag : Tag
  ): Promise<IStandardResponse<Tag>> => {
    try {
      const { data } = await warehouseApiClient.post<
        IStandardResponse<Tag>
      >(`${EWarehouseEndPoint.CREATE_TAG}`, {
        ...tag,
      });
      if (!data) {
        throw new Error("No data returned from the server");
      }
      toast.success("Tag successfully created.");
      return data;
    } catch (error: any) {
      toast.error(`${error.error.message}` || `${error.message}` || "Create tag failed" );
      throw new Error(error);
    }
  },

  deleteTag: async (
    id: number
  ): Promise<IStandardResponse<Tag>> => {
    try {
      const { data } = await warehouseApiClient.delete<
        IStandardResponse<Tag>
      >(`${EWarehouseEndPoint.DELETE_TAG}${id}`);
      if (!data) {
        throw new Error("No data returned from the server");
      }
      toast.success("Tag successfully deleted.");
      return data;
    } catch (error: any) {
      toast.error(
        `${error.message}` ||
          `${error.error.message}` ||
          "Delete tag type failed"
      );
      throw new Error(error);
    }
  },

  searchReferences: async (query: string): Promise<string[]> => {
    try {
      const { data } = await warehouseApiClient.get<{
        success: boolean;
        data: { data: string[] };
      }>(`${EWarehouseEndPoint.REFERENCE}/search`, {
        params: { q: query },
      });
      return data.data.data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },

  createAddManuelGarment: async <T>(
    input: any
  ): Promise<IStandardResponse<T>> => {
    try {
      const { data } = await warehouseApiClient.post<IStandardResponse<T>>(
        EWarehouseEndPoint.GARMENT_CREATE,
        input
      );

      if (!data) {
        throw new Error("No data returned from the server");
      }

      toast.success("Garment stock successfully created.");
      return data;
    } catch (error: any) {
      toast.error(
        error?.error?.message ||
        error?.message ||
        "Garment creation failed"
      );
      throw new Error(throwError(error));
    }
  },

  createAdjustment: async (input: {
    category: string;
    stock_id: number;
    new_quantity: number;
    adjustment_type: string;
    reason_comment?: string;
    damage_location?: string;
  }) => {
    try {
      const { data } = await warehouseApiClient.post(EWarehouseEndPoint.ADJUSTMENT, input);
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },

  getAdjustments: async (
    page: number,
    limit: number,
    search: Record<string, any>,
    sort: string,
    global_search?: string
  ) => {
    try {
      const { data } = await warehouseApiClient.post(`${EWarehouseEndPoint.ADJUSTMENT}/list`, {
        page, limit, search, sort, global_search,
      });
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },

  reviewAdjustment: async (id: number, action: "approve" | "reject" | "cancel", comment?: string) => {
    try {
      const { data } = await warehouseApiClient.patch(
        `${EWarehouseEndPoint.ADJUSTMENT}/${id}/${action}`,
        { comment }
      );
      return data;
    } catch (error) {
      throw new Error(throwError(error));
    }
  },

};
